import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AppMenu } from './app.menu';
import { LayoutService } from '@/layout/service/layout.service';
import { RouterModule } from '@angular/router';
import {
  AppStateService,
  SelectedAccountScope,
} from '@/services/app-state.service';
import { Subscription } from 'rxjs';
import { AppAccountSelectorComponent } from './app.accountselector';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { AppAccountLogoComponent } from './app.accountlogo';

@Component({
  selector: '[app-sidebar]',
  standalone: true,
  imports: [AppMenu, RouterModule, ButtonModule, AppAccountLogoComponent],
  providers: [DialogService, DynamicDialogConfig],
  template: `
    <div
      class="layout-sidebar"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
    >
      <div class="sidebar-header flex-col">
        <app-account-logo
          variant="image"
          [accountLogoScope]="selectedAccountScope"
        />

        <div class="px-3 mt-3">
          @if (selectedAccountScope?.name) {
            <div class="px-2">{{ selectedAccountScope?.name }}</div>
          }
        </div>

        <div class="px-3 mt-5 w-full">
          <p-button
            [label]="'Select Location'"
            severity="secondary"
            icon="pi pi-angle-down"
            iconPos="right"
            size="small"
            styleClass="w-full justify-content-between"
            variant="outlined"
            (click)="open()"
          ></p-button>
        </div>
      </div>

      <button
        class="layout-sidebar-anchor p-link z-2"
        type="button"
        (click)="anchor()"
      ></button>

      <div #menuContainer class="layout-menu-container">
        <app-menu></app-menu>
      </div>
    </div>
  `,
})
export class AppSidebar implements OnInit, OnDestroy {
  timeout: ReturnType<typeof setTimeout> | null = null;
  selectedAccountScope: SelectedAccountScope | null = null;
  private dialogService = inject(DialogService);
  ref: DynamicDialogRef | null = null;
  private subs: Subscription[] = [];

  @ViewChild('menuContainer') menuContainer!: ElementRef;

  constructor(
    private appStateService: AppStateService,
    public layoutService: LayoutService,
    public el: ElementRef,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.appStateService.selectedAccountScope$.subscribe((accountScope) => {
        this.selectedAccountScope = accountScope;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  onMouseEnter() {
    if (!this.layoutService.layoutState().anchored) {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      this.layoutService.layoutState.update((state) => {
        if (!state.sidebarActive) {
          return {
            ...state,
            sidebarActive: true,
          };
        }
        return state;
      });
    }
  }

  onMouseLeave() {
    if (!this.layoutService.layoutState().anchored) {
      if (!this.timeout) {
        this.timeout = setTimeout(() => {
          this.layoutService.layoutState.update((state) => {
            if (state.sidebarActive) {
              return {
                ...state,
                sidebarActive: false,
              };
            }
            return state;
          });
        }, 300);
      }
    }
  }

  open() {
    this.ref = this.dialogService.open(AppAccountSelectorComponent, {
      modal: true,
      header: 'Select Location',
      closable: true,
      styleClass: 'min-h-[70%]',
      maximizable: true,
      width: '40rem',
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      // Couldn't find a better way to override min / max icon styling.
      style: {
        '--p-button-text-primary-color': 'var(--p-button-text-secondary-color)',
        '--p-button-text-primary-hover-color':
          'var(--p-button-text-secondary-hover-color)',
        '--p-button-text-primary-hover-background':
          'var(--p-button-text-secondary-hover-background)',
        '--p-button-text-primary-active-background':
          'var(--p-button-text-secondary-active-background)',
      },
    });
  }

  anchor() {
    this.layoutService.layoutState.update((state) => ({
      ...state,
      anchored: !state.anchored,
    }));
  }
}
